import React, { useState, useEffect } from 'react';
import { workshopsData as mockWorkshopsData } from '../data/mock';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WorkshopsSection = ({ data }) => {
  const workshopsData = data || mockWorkshopsData;
  const workshopImages = workshopsData.images || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || !workshopImages.length) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) =>
        prev === workshopImages.length - 1 ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, workshopImages.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? workshopImages.length - 1 : prev - 1
    );
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex((prev) =>
      prev === workshopImages.length - 1 ? 0 : prev + 1
    );
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section
      id="workshops"
      className="min-h-screen bg-gradient-to-br from-[#F5B041] to-[#F5B041]/70 py-20 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <div 
          className="inline-block bg-[#1A532F] text-white px-6 py-3 rounded-full mb-8 font-bold text-sm"
          style={{ fontFamily: "'Press Start 2P', monospace" }}
        >
          {workshopsData.title}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <div>
            <p className="text-[#1A532F] text-xl md:text-2xl leading-relaxed font-light mb-8">
              {workshopsData.description}
            </p>
          </div>

          {/* Right: Image Carousel */}
          <div className="relative">
            <div className="bg-white/50 backdrop-blur-sm border-4 border-[#1A532F] rounded-2xl overflow-hidden">
              {/* Carousel Images */}
              <div className="relative h-[500px] overflow-hidden">
                {workshopImages.map((image, index) => (
                  <div
                    key={image.id}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                      index === currentIndex
                        ? 'opacity-100 translate-x-0'
                        : index < currentIndex
                        ? 'opacity-0 -translate-x-full'
                        : 'opacity-0 translate-x-full'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.caption}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A532F]/80 to-transparent p-6">
                      <p className="text-white font-medium">{image.caption}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              {workshopImages.length > 1 && (
                <>
                  <button
                    onClick={goToPrevious}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-[#1A532F] p-3 rounded-full transition-all hover:scale-110 shadow-lg z-10"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-[#1A532F] p-3 rounded-full transition-all hover:scale-110 shadow-lg z-10"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}

              {/* Dot Indicators */}
              {workshopImages.length > 1 && (
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                  {workshopImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`transition-all duration-300 ${
                        index === currentIndex
                          ? 'w-8 h-3 bg-white'
                          : 'w-3 h-3 bg-white/50 hover:bg-white/75'
                      } rounded-full`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkshopsSection;

